import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { HERO_IMG, TRUST_IMG } from "../../lib/assets";
import { scrollToId } from "../../hooks/useLenis";

const scrollTo = (id) => scrollToId(id);

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yImg2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden bg-sage grain pt-[72px]">
      {/* soft background shapes */}
      <div className="absolute -top-40 -right-40 h-[38rem] w-[38rem] rounded-full bg-leaf/10 blur-3xl" />
      <div className="absolute top-1/3 -left-40 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center pt-10 lg:pt-16 pb-24">
        {/* left copy */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/60 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-forest"
            data-testid="hero-badge"
          >
            <Sparkle size={15} weight="fill" className="text-gold" />
            {t.hero.badge}
          </motion.div>

          <h1 className="mt-7 font-serif text-6xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-forest">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {i === 1 ? <em className="text-leaf not-italic font-medium">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-md text-base sm:text-lg text-forest/70 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("quote")}
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-bold text-white hover:bg-forest-dark transition-colors"
              data-testid="hero-quote-cta"
            >
              {t.hero.cta1}
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-7 py-4 text-sm font-bold text-forest hover:bg-forest hover:text-white transition-colors"
              data-testid="hero-services-cta"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[["500+", t.hero.stat1], ["9+", t.hero.stat2], ["100%", t.hero.stat3]].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-4xl text-forest">{n}</div>
                <div className="mt-1 text-xs text-forest/60 leading-snug">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* right images with parallax */}
        <div className="lg:col-span-6 relative h-[420px] sm:h-[540px]">
          <motion.div
            style={{ y: yImg, scale }}
            className="absolute right-0 top-0 w-[75%] h-[75%] rounded-[2rem] overflow-hidden ring-1 ring-forest/10 shadow-2xl"
          >
            <img src={HERO_IMG} alt="Salon propre" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            style={{ y: yImg2 }}
            className="absolute left-0 bottom-0 w-[52%] h-[55%] rounded-[1.5rem] overflow-hidden ring-4 ring-sage shadow-2xl"
          >
            <img src={TRUST_IMG} alt="Nettoyage professionnel" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="absolute left-2 top-6 sm:left-6 sm:top-10 rounded-2xl bg-forest text-white px-5 py-4 shadow-xl"
          >
            <div className="flex items-center gap-2 text-gold text-xs font-bold uppercase tracking-widest">
              <CheckCircle size={16} weight="fill" /> {t.why.tagline}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
